import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Map, ExternalLink, Loader2, Lock } from "lucide-react";
import Header from "../../components/Header/Header";
import Footer from "../../components/Footer/Footer";
import Seo from "../../components/Seo/Seo";
import { BASE_URL } from "../../constants/urls";
import { buildShopUrl } from "../../utils/shopUrl.util";
import {
  PAGES,
  HTML_SITEMAP_SECTIONS,
  absoluteUrl,
} from "../../seo/seo.config";
import { breadcrumbSchema, itemListSchema } from "../../seo/structuredData";
import "./SitemapScreen.css";

export default function SitemapScreen() {
  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${BASE_URL}/customer/shops`);
        const data = await res.json();
        if (!cancelled) setShops(data?.shops || []);
      } catch {
        if (!cancelled) setShops([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const salonLinks = useMemo(
    () =>
      shops
        .map((shop) => ({
          id: shop.id,
          name: shop.parlourName || shop.name || "Salon",
          address: shop.address,
          url: buildShopUrl(shop),
        }))
        .filter((s) => s.url && s.url !== "/shops")
        .sort((a, b) => a.name.localeCompare(b.name)),
    [shops]
  );

  const totalPages = useMemo(
    () =>
      HTML_SITEMAP_SECTIONS.reduce((n, s) => n + s.pages.length, 0) +
      salonLinks.length,
    [salonLinks]
  );

  const jsonLd = useMemo(
    () => [
      breadcrumbSchema([{ name: "Sitemap", path: PAGES.sitemap.path }]),
      itemListSchema({
        name: "Orucom Sitemap",
        description: PAGES.sitemap.description,
        path: PAGES.sitemap.path,
        items: [
          // Only the pages a crawler should actually follow — the logged-in
          // ones are listed for humans but are noindex, so advertising them
          // in structured data would be noise.
          ...HTML_SITEMAP_SECTIONS.flatMap((s) =>
            s.pages
              .filter((p) => !p.noindex)
              .map((p) => ({ name: p.label, url: p.canonical || p.path }))
          ),
          ...salonLinks.map((s) => ({ name: s.name, url: s.url })),
        ],
      }),
    ],
    [salonLinks]
  );

  return (
    <div className="sitemap-page">
      <Seo
        title={PAGES.sitemap.title}
        description={PAGES.sitemap.description}
        keywords={PAGES.sitemap.keywords}
        canonical={PAGES.sitemap.path}
        jsonLd={jsonLd}
      />
      <Header />

      <div className="sitemap-hero">
        <span className="sitemap-hero-tag">
          <Map size={14} /> Site Map
        </span>
        <h1>Everything on Orucom</h1>
        <p>
          A complete map of the site — every section, account page, salon
          listing and policy page in one place.
        </p>
        <a
          className="sitemap-xml-link"
          href={absoluteUrl("/sitemap.xml")}
          target="_blank"
          rel="noopener noreferrer"
        >
          View the XML sitemap for search engines
          <ExternalLink size={14} />
        </a>
      </div>

      <div className="sitemap-body">
        <div className="sitemap-grid">
          {HTML_SITEMAP_SECTIONS.map(({ group, pages }) => (
            <section key={group} className="sitemap-group">
              <h2 className="sitemap-group-title">{group}</h2>
              <ul className="sitemap-links">
                {pages.map((page) => (
                  <li key={page.path}>
                    <Link to={page.canonical || page.path}>
                      {page.label}
                      {page.requiresLogin && (
                        <Lock
                          size={11}
                          className="sitemap-lock"
                          aria-label="Sign-in required"
                        />
                      )}
                    </Link>
                    <span className="sitemap-link-desc">{page.description}</span>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>

        <section className="sitemap-group sitemap-salons">
          <h2 className="sitemap-group-title">
            Salon Listings
            {!loading && salonLinks.length > 0 && (
              <span className="sitemap-count">{salonLinks.length}</span>
            )}
          </h2>

          {loading ? (
            <p className="sitemap-loading">
              <Loader2 size={16} className="sitemap-spin" /> Loading salons…
            </p>
          ) : salonLinks.length === 0 ? (
            <p className="sitemap-empty">
              No salon listings are available right now.{" "}
              <Link to="/shops">Browse all salons</Link>.
            </p>
          ) : (
            <ul className="sitemap-links sitemap-links--columns">
              {salonLinks.map((salon) => (
                <li key={salon.id}>
                  <Link to={salon.url}>{salon.name}</Link>
                  {salon.address && (
                    <span className="sitemap-link-desc">{salon.address}</span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>

        {!loading && (
          <p className="sitemap-total">
            {totalPages} pages listed.{" "}
            <Lock size={11} className="sitemap-lock" /> marks pages that need
            you to be signed in.
          </p>
        )}
      </div>

      <Footer />
    </div>
  );
}
